/**
 * Runtime state of Regions (effects/regions.ts): which regions are shown, their fades, triggers,
 * animated looks and the effect + uniforms that draw them. It generalises the annotation layers
 * runtime to every region (a focus region is a region that dims the rest). Free of engine and DOM
 * objects so it is unit-testable; createViewer supplies the host and draws the Layers menu from
 * `layersMenu`.
 *
 * Visibility: drawn = enabled AND (editor preview OR visitor/API choice OR (no triggers ?
 * startsOn : trigger controller)).
 * - A region starts as `startsOn` says (default on). A region with triggers starts off and its
 *   triggers show it (a hotspot click toggles it).
 * - Visitor / API choices are mirrored into the trigger controller, so a later hotspot click
 *   toggles from the state the visitor sees and a held condition doesn't immediately undo it.
 * - The editor starts every untriggered region shown, so creators see what they draw (its eye
 *   buttons switch them through setVisible), and runs triggers as the published scene does; the
 *   region being edited stays drawn (`setPreview`).
 * - 'single' menu mode: showing a menu region (from the menu, the API or a trigger) hides the
 *   other menu regions. Regions outside the menu are unaffected. "Show all" shows every menu region.
 * - Every switch fades over REGION_FADE_MS; nothing recompiles. Only editing areas recompiles.
 * - Keyframe animation overrides a region's colour, colour strength and dim look, and the
 *   transform of a region whose only area is a sphere / box. Visibility still decides whether it
 *   is drawn: an animated region that is hidden stays hidden.
 */
import type { EditorAreaBounds } from '../effects/area-shape';
import { normalizeRegion, type RegionShape } from '../effects/region-shape';
import { REGION_FADE_MS, type RegionsConfig } from '../effects/regions';
import {
    buildRegionsEffect,
    regionRgb,
    regionSurround,
    resolveRegions,
    resolveRegionsMenu,
    writeRegionAreaUniform,
    writeRegionsFrame,
    type ResolvedRegion,
    type ResolvedRegionVolume,
    type ResolvedRegions,
    type ResolvedRegionsMenu,
} from '../effects/regions-effect';
import type { RegionColorEffect } from '../effects/splat-region-effects';
import { TriggerController, type TriggeredItem } from './overlays';

export interface RegionsRuntimeHost {
    /** Install (or with undefined, remove) the splat shader effect. Recompiles: called only when areas change. */
    setEffect(effect: RegionColorEffect | undefined): void;
    /** A uniform changed: make sure a frame renders (the idle scheduler may be paused). */
    requestRender(): void;
}

export interface RegionsRuntimeOptions {
    /** Editor viewport: untriggered regions start shown whatever startsOn says; triggers still run. */
    editor?: boolean;
    /** Fade duration in ms (0 = instant). Default REGION_FADE_MS. */
    fadeMs?: number;
}

/** A region as the API and the Layers menu see it. */
export interface RegionState {
    id: string;
    name: string;
    color: string;
    /** The visitor / API / trigger state (what getVisibleRegions lists). */
    visible: boolean;
    inLayersMenu: boolean;
    dimsOutside: boolean;
    section?: string;
    description?: string;
    showArea: boolean;
    /** Sum of its outline areas, scene units squared. */
    area: number;
    triggered: boolean;
    /**
     * The focus region ids it was converted from (regionsFromLegacy): one per area named
     * `<id>:area`. Several when focus regions that were simply on became one highlight.
     */
    focusIds: string[];
}

export interface RegionVisibilityChange {
    id: string;
    visible: boolean;
}

type Listener = (visibleIds: string[], changes: RegionVisibilityChange[]) => void;

/** A region's look: colour 0..1, colour strength 0..1, the brightness / saturation of the rest 0..1. */
export interface RegionLook {
    r: number;
    g: number;
    b: number;
    colorStrength: number;
    dimBrightness: number;
    dimSaturation: number;
}

/** Keyframe channels of entity type 'region' that change its look. */
export const REGION_LOOK_PROPERTIES = ['region.color.r', 'region.color.g', 'region.color.b', 'region.colorStrength', 'region.dimBrightness', 'region.dimSaturation'] as const;
/** Keyframe channels of a region whose only area is a sphere / box: that area's transform. */
export const REGION_SHAPE_PROPERTIES = [
    'region.position.x', 'region.position.y', 'region.position.z',
    'region.rotation.x', 'region.rotation.y', 'region.rotation.z',
    'region.size.x', 'region.size.y', 'region.size.z',
    'region.feather',
] as const;
const LOOK_KEYS: Record<(typeof REGION_LOOK_PROPERTIES)[number], keyof RegionLook> = {
    'region.color.r': 'r',
    'region.color.g': 'g',
    'region.color.b': 'b',
    'region.colorStrength': 'colorStrength',
    'region.dimBrightness': 'dimBrightness',
    'region.dimSaturation': 'dimSaturation',
};
const ANIMATABLE = new Set<string>([...REGION_LOOK_PROPERTIES, ...REGION_SHAPE_PROPERTIES]);

/** What the Layers menu (layersPanel.ts) reads and calls. */
export interface LayersMenuEntry {
    id: string;
    name: string;
    color: string;
    section?: string;
    description?: string;
    showArea: boolean;
    area: number;
}
export interface LayersMenuSource {
    readonly panel: ResolvedRegionsMenu;
    getGroups(): LayersMenuEntry[];
    isVisible(id: string): boolean;
    allVisible(): boolean;
    select(id: string): void;
    selectAll(): void;
    onChange(listener: () => void): () => void;
}

const inMenu = (r: ResolvedRegion) => r.inLayersMenu;
const DEFAULT_MENU = resolveRegionsMenu(undefined);

export class RegionsRuntime {
    private resolved: ResolvedRegions | undefined;
    /** The Layers menu settings (title, "open at start"), read even when no region is drawn. */
    private menuSettings: ResolvedRegionsMenu = DEFAULT_MENU;
    private readonly uniforms: Record<string, Float32Array> = {};
    /** Editor bounds of the installed effect (in their region's colour), refilled in place as regions show and hide. */
    private readonly bounds: EditorAreaBounds[] = [];
    /** Editor: the region overlay draws the areas itself (the Regions panel is open), so no bounds. */
    private boundsHidden = false;
    private installedKey: string | null = null;
    /** Target visibility per region id; survives config edits for ids that remain. */
    private readonly visible = new Map<string, boolean>();
    /** Current fade 0..1 per region id. */
    private readonly fade = new Map<string, number>();
    /** Whether each region had triggers at the last setConfig. */
    private readonly triggered = new Map<string, boolean>();
    /** Editor: regions kept drawn whatever their state, by preview slot (see setPreview). */
    private readonly previews = new Map<string, string>();
    /**
     * Regions hidden by an explicit setVisible / toggle (in the editor: the Regions panel's eye). A
     * preview keeps a region drawn whatever its triggers say, but not when the creator hid it.
     */
    private readonly hiddenByUser = new Set<string>();
    /** Animated channels per region id (region.* keys), on top of the authored values. */
    private readonly animated = new Map<string, Map<string, number>>();
    private readonly listeners = new Set<Listener>();
    private readonly fadeMs: number;
    private readonly editor: boolean;
    /** True while change() mirrors a state into the trigger controller (its callback must only record it). */
    private syncingTriggers = false;
    private destroyed = false;
    /** Drives regions that have triggers; createViewer updates it with the other trigger controllers. */
    readonly triggers: TriggerController<TriggeredItem>;
    /** The visitor Layers menu's view of the regions in it. */
    readonly layersMenu: LayersMenuSource;

    constructor(private readonly host: RegionsRuntimeHost, options: RegionsRuntimeOptions = {}) {
        this.editor = options.editor === true;
        this.fadeMs = Math.max(0, options.fadeMs ?? REGION_FADE_MS);
        this.triggers = new TriggerController<TriggeredItem>(
            {
                // While change() mirrors a visitor / "Show all" decision into the controller, the
                // callback must not run the selection logic again (in single mode that would hide
                // regions the caller just showed); change() records the state itself.
                show: (item) => {
                    if (!this.syncingTriggers) this.apply(item.id, true, 'trigger');
                },
                hide: (item) => {
                    if (!this.syncingTriggers) this.apply(item.id, false, 'trigger');
                },
                // Edits to a region's triggers keep its current state; the next frame re-evaluates.
                update: () => true,
            },
            { hotspotToggles: true },
        );
        const self = this;
        this.layersMenu = {
            get panel() {
                return self.menu;
            },
            getGroups: () => this.getMenuRegions().map((r) => ({
                id: r.id,
                name: r.name,
                color: r.color,
                ...(r.section ? { section: r.section } : {}),
                ...(r.description ? { description: r.description } : {}),
                showArea: r.showArea,
                area: r.area,
            })),
            isVisible: (id) => this.isVisible(id),
            allVisible: () => this.allMenuVisible(),
            select: (id) => {
                this.select(id);
            },
            selectAll: () => this.selectAll(),
            onChange: (listener) => this.onChange(() => listener()),
        };
    }

    /** Replace the authored regions (scene load, every editor edit). Recompiles only when the areas changed. */
    setConfig(config: RegionsConfig | undefined | null): void {
        if (this.destroyed) return;
        const before = this.snapshot();
        const previousIds = new Set(this.visible.keys());
        this.resolved = resolveRegions(config ?? undefined);
        this.menuSettings = this.resolved?.menu ?? resolveRegionsMenu(config && typeof config === 'object' ? config.menu : undefined);
        const regions = this.resolved?.regions ?? [];
        const ids = new Set(regions.map((r) => r.id));
        for (const id of previousIds) {
            if (!ids.has(id)) {
                this.visible.delete(id);
                this.hiddenByUser.delete(id);
                this.fade.delete(id);
                this.triggered.delete(id);
                this.animated.delete(id);
            }
        }
        // A new region, or one that gained or lost its triggers, starts from its load state: off
        // until a trigger shows it, otherwise as startsOn says (the editor shows it).
        for (const r of regions) {
            const triggered = !!r.triggers;
            if (this.visible.has(r.id) && this.triggered.get(r.id) === triggered) continue;
            this.triggered.set(r.id, triggered);
            this.visible.set(r.id, !triggered && (this.editor || r.startsOn));
            this.fade.set(r.id, this.shown(r.id) ? 1 : 0);
        }
        // A one-at-a-time menu shows one of its regions from the start too (the first one on).
        if (!this.editor && this.menu.selectionMode === 'single') {
            let kept = false;
            for (const r of regions) {
                if (!r.inLayersMenu || !this.visible.get(r.id)) continue;
                if (kept && !previousIds.has(r.id)) {
                    this.visible.set(r.id, false);
                    this.fade.set(r.id, 0);
                }
                kept = true;
            }
        }
        // The controller only records here: a region that just lost its triggers must not be
        // hidden as "removed", and its state comes from the reset above. Triggered regions then
        // take the controller's state (an edit re-evaluates a held condition against the last frame).
        this.syncingTriggers = true;
        try {
            this.triggers.setItems(regions.filter((r) => r.triggers).map((r) => ({ id: r.id, enabled: true, triggers: r.triggers })));
        } finally {
            this.syncingTriggers = false;
        }
        for (const r of regions) {
            if (!r.triggers || this.triggers.isVisible(r.id) === this.visible.get(r.id)) continue;
            this.visible.set(r.id, this.triggers.isVisible(r.id));
            if (this.fadeMs === 0) this.fade.set(r.id, this.shown(r.id) ? 1 : 0);
        }
        const effect = buildRegionsEffect(this.resolved, this.uniforms, this.bounds);
        // Which objects a splat area names isn't in the source, but reaches the GPU through `set`.
        const key = effect ? `${effect.glsl}\n${effect.wgsl}\n${JSON.stringify(effect.objectSets ?? [])}` : null;
        this.writeUniforms();
        if (key !== this.installedKey) {
            this.installedKey = key;
            this.host.setEffect(effect);
        }
        this.host.requestRender();
        this.emit(before);
    }

    /** The Layers menu settings (the saved ones even without regions; defaults without any). */
    get menu(): ResolvedRegionsMenu {
        return this.menuSettings;
    }

    hasRegions(): boolean {
        return !!this.resolved?.regions.length;
    }

    /** The Layers menu has something to show: it isn't switched off and a region is in it. */
    hasMenu(): boolean {
        return this.menu.show && this.getMenuRegions().length > 0;
    }

    /** Editor bounds wanted (RegionsConfig.showBounds). */
    get showBounds(): boolean {
        return this.resolved?.showBounds !== false;
    }

    /**
     * Editor: leave the bounds out while the region overlay (setRegionEditorOverlay with
     * `outlines`) draws every area itself, selected one included, so nothing is drawn twice.
     */
    setBoundsHidden(hidden: boolean): void {
        if (this.boundsHidden === hidden) return;
        this.boundsHidden = hidden;
        this.writeUniforms();
        this.host.requestRender();
    }

    getRegions(): RegionState[] {
        return (this.resolved?.regions ?? []).map((r) => ({
            id: r.id,
            name: r.name,
            color: r.color,
            visible: this.visible.get(r.id) === true,
            inLayersMenu: r.inLayersMenu,
            dimsOutside: !!r.dim,
            ...(r.section ? { section: r.section } : {}),
            ...(r.description ? { description: r.description } : {}),
            showArea: r.showArea,
            area: r.area,
            triggered: !!r.triggers,
            focusIds: (this.resolved?.areas ?? []).filter((a) => a.regionId === r.id && a.id.endsWith(':area')).map((a) => a.id.slice(0, -':area'.length)),
        }));
    }

    getMenuRegions(): RegionState[] {
        return this.getRegions().filter((r) => r.inLayersMenu);
    }

    /** Ids of the regions switched on (visitor / API / triggers), in region order. */
    getVisibleIds(): string[] {
        return (this.resolved?.regions ?? []).filter((r) => this.visible.get(r.id)).map((r) => r.id);
    }

    hasRegion(id: string): boolean {
        return this.visible.has(id);
    }

    isVisible(id: string): boolean {
        return this.visible.get(id) === true;
    }

    /** Drawn: switched on, or kept on by an editor preview. */
    isShown(id: string): boolean {
        return this.shown(id);
    }

    /** Every menu region is on (the menu's "Show all" entry is checked). */
    allMenuVisible(): boolean {
        const menu = (this.resolved?.regions ?? []).filter(inMenu);
        return menu.length > 0 && menu.every((r) => this.visible.get(r.id));
    }

    /** Switch one region on or off. In 'single' menu mode, showing a menu region hides the other menu regions. False for an unknown id. */
    setVisible(id: string, visible: boolean): boolean {
        if (this.destroyed || !this.visible.has(id)) return false;
        this.apply(id, visible, 'user');
        return true;
    }

    toggle(id: string): boolean {
        return this.setVisible(id, !this.isVisible(id));
    }

    /**
     * Show only `id` (null: hide them all) among the regions `among` selects (default: every
     * region); the others keep their state.
     */
    showOnly(id: string | null, among?: (region: ResolvedRegion) => boolean): boolean {
        if (this.destroyed || (id !== null && !this.visible.has(id))) return false;
        const before = this.snapshot();
        for (const r of this.resolved?.regions ?? []) if (r.id !== id && (!among || among(r))) this.change(r.id, false, true);
        if (id !== null) this.change(id, true, true);
        this.commit(before);
        return true;
    }

    /** Switch every region `among` selects (default: every region) on or off. */
    setAll(visible: boolean, among?: (region: ResolvedRegion) => boolean): void {
        if (this.destroyed) return;
        const before = this.snapshot();
        for (const r of this.resolved?.regions ?? []) if (!among || among(r)) this.change(r.id, visible, true);
        this.commit(before);
    }

    /** A Layers menu click on a region, following the selection mode. */
    select(id: string): boolean {
        const region = this.region(id);
        if (!region) return false;
        if (this.menu.selectionMode === 'multiple' || !region.inLayersMenu) return this.toggle(id);
        // Single: picking the only shown menu region again clears it, so visitors can see the bare
        // scan; picking one while several are shown ("Show all") isolates it.
        const shownInMenu = (this.resolved?.regions ?? []).filter((r) => r.inLayersMenu && this.visible.get(r.id));
        if (this.isVisible(id) && shownInMenu.length === 1) return this.showOnly(null, inMenu);
        return this.showOnly(id, inMenu);
    }

    /** A Layers menu click on "Show all": shows every menu region, or hides them all when they already are. */
    selectAll(): void {
        this.setAll(!this.allMenuVisible(), inMenu);
    }

    /**
     * Editor: keep region `id` drawn whatever its state (null: none). Separate slots let two
     * callers (the region being edited, a legacy layer preview) keep one each.
     */
    setPreview(id: string | null, slot = 'region'): void {
        if (this.destroyed || (this.previews.get(slot) ?? null) === id) return;
        if (id === null) this.previews.delete(slot);
        else this.previews.set(slot, id);
        if (this.fadeMs === 0) for (const r of this.resolved?.regions ?? []) this.fade.set(r.id, this.shown(r.id) ? 1 : 0);
        this.writeUniforms();
        this.host.requestRender();
    }

    /** A region's current look (animated values where set), or null for an unknown region. */
    getLook(id: string): RegionLook | null {
        const r = this.region(id);
        if (!r) return null;
        const [red, green, blue] = regionRgb(r.color);
        const a = this.animated.get(id);
        const value = (prop: string, fallback: number) => a?.get(prop) ?? fallback;
        return {
            r: value('region.color.r', red),
            g: value('region.color.g', green),
            b: value('region.color.b', blue),
            colorStrength: value('region.colorStrength', r.colorStrength),
            dimBrightness: value('region.dimBrightness', r.dim?.brightness ?? 1),
            dimSaturation: value('region.dimSaturation', r.dim?.saturation ?? 1),
        };
    }

    /** The sphere / box a region's keyframes move (its only area), animated values applied; null otherwise. */
    getAnimatedShape(id: string): RegionShape | null {
        const area = this.volumeOf(id);
        if (!area) return null;
        const o = this.animated.get(id);
        const base = area.region;
        if (!o || !o.size) return base;
        const v = (prefix: string, b: { x: number; y: number; z: number }) => ({ x: o.get(`${prefix}.x`) ?? b.x, y: o.get(`${prefix}.y`) ?? b.y, z: o.get(`${prefix}.z`) ?? b.z });
        return normalizeRegion({
            ...base,
            position: v('region.position', base.position),
            rotation: v('region.rotation', base.rotation),
            size: v('region.size', base.size),
            feather: o.get('region.feather') ?? base.feather,
        });
    }

    /**
     * Keyframe animation: set some `region.*` channels of one region (unknown channels are
     * ignored; look values clamp to 0..1). Only uniforms change, so this is cheap enough to call
     * every frame. Survives config edits that keep the region.
     */
    setAnimatedValues(id: string, values: ReadonlyMap<string, number> | Record<string, number>): void {
        if (this.destroyed) return;
        const entries = values instanceof Map ? [...values.entries()] : Object.entries(values);
        let current = this.animated.get(id);
        for (const [prop, raw] of entries) {
            if (!ANIMATABLE.has(prop) || typeof raw !== 'number' || !Number.isFinite(raw)) continue;
            const v = prop in LOOK_KEYS ? Math.min(1, Math.max(0, raw)) : raw;
            if (!current) {
                current = new Map();
                this.animated.set(id, current);
            }
            current.set(prop, v);
        }
        this.writeUniforms();
        this.host.requestRender();
    }

    /**
     * Current values of every channel a region animates (look, and the transform of a region
     * whose only area is a sphere / box), animated where set. Null for an unknown region.
     */
    readAnimatedValues(id: string): Record<string, number> | null {
        const look = this.getLook(id);
        if (!look) return null;
        const values: Record<string, number> = {
            'region.color.r': look.r,
            'region.color.g': look.g,
            'region.color.b': look.b,
            'region.colorStrength': look.colorStrength,
            'region.dimBrightness': look.dimBrightness,
            'region.dimSaturation': look.dimSaturation,
        };
        const shape = this.getAnimatedShape(id);
        if (shape) {
            Object.assign(values, {
                'region.position.x': shape.position.x, 'region.position.y': shape.position.y, 'region.position.z': shape.position.z,
                'region.rotation.x': shape.rotation.x, 'region.rotation.y': shape.rotation.y, 'region.rotation.z': shape.rotation.z,
                'region.size.x': shape.size.x, 'region.size.y': shape.size.y, 'region.size.z': shape.size.z,
                'region.feather': shape.feather,
            });
        }
        return values;
    }

    /** Drop animated channels (all of them, or only `props`) of one region, or of every region when `id` is null. */
    clearAnimatedValues(id: string | null, props?: ReadonlyArray<string>): void {
        if (this.destroyed) return;
        if (id === null) this.animated.clear();
        else if (!props) this.animated.delete(id);
        else {
            const current = this.animated.get(id);
            if (current) for (const p of props) current.delete(p);
            if (current && current.size === 0) this.animated.delete(id);
        }
        this.writeUniforms();
        this.host.requestRender();
    }

    /** Listen for visibility changes: the visible ids and the regions whose state changed (none on a plain config edit). */
    onChange(listener: Listener): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /** Advance fades; returns true while one is still running (call every frame while true). */
    tick(dtMs: number): boolean {
        if (!this.resolved) return false;
        let running = false;
        for (const r of this.resolved.regions) {
            const target = this.shown(r.id) ? 1 : 0;
            const current = this.fade.get(r.id) ?? target;
            if (current === target) continue;
            const step = this.fadeMs > 0 ? Math.max(0, dtMs) / this.fadeMs : 1;
            const next = target > current ? Math.min(target, current + step) : Math.max(target, current - step);
            this.fade.set(r.id, next);
            if (next !== target) running = true;
        }
        this.writeUniforms();
        this.host.requestRender();
        return running;
    }

    /** True while any region is part way through its fade. */
    isFading(): boolean {
        for (const r of this.resolved?.regions ?? []) {
            if ((this.fade.get(r.id) ?? 0) !== (this.shown(r.id) ? 1 : 0)) return true;
        }
        return false;
    }

    /** A region's current fade 0..1 (0 for an unknown region). */
    getFade(id: string): number {
        return this.fade.get(id) ?? 0;
    }

    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.triggers.destroy();
        this.visible.clear();
        this.fade.clear();
        this.triggered.clear();
        this.animated.clear();
        this.previews.clear();
        this.listeners.clear();
        if (this.installedKey !== null) this.host.setEffect(undefined);
        this.installedKey = null;
        this.resolved = undefined;
        this.bounds.length = 0;
    }

    // ── internals ──────────────────────────────────────────────────────────────────────────

    private region(id: string): ResolvedRegion | undefined {
        return this.resolved?.regions.find((r) => r.id === id);
    }

    private volumeOf(id: string): ResolvedRegionVolume | null {
        const index = this.resolved?.regions.findIndex((r) => r.id === id) ?? -1;
        if (index < 0) return null;
        const own = this.resolved!.areas.filter((a) => a.regionIndex === index);
        return own.length === 1 && (own[0].kind === 'sphere' || own[0].kind === 'box') ? (own[0] as ResolvedRegionVolume) : null;
    }

    private shown(id: string): boolean {
        if (this.visible.get(id) === true) return true;
        if (this.hiddenByUser.has(id)) return false;
        for (const previewed of this.previews.values()) if (previewed === id) return true;
        return false;
    }

    private snapshot(): Map<string, boolean> {
        return new Map(this.visible);
    }

    /**
     * One visibility change from the menu / API ('user') or from a trigger. A user change on a
     * triggered region goes through its controller, so a later hotspot click toggles from the
     * state the visitor sees and a held condition doesn't immediately undo it.
     */
    private apply(id: string, visible: boolean, source: 'user' | 'trigger'): void {
        const region = this.region(id);
        if (!region || this.destroyed) return;
        const before = this.snapshot();
        if (visible && region.inLayersMenu && this.menu.selectionMode === 'single' && !this.editor) {
            // The others' controllers must agree they are off, whoever solo'd this one.
            for (const r of this.resolved?.regions ?? []) if (r.id !== id && r.inLayersMenu) this.change(r.id, false, true);
        }
        this.change(id, visible, source === 'user');
        if (source === 'user') {
            if (visible) this.hiddenByUser.delete(id);
            else this.hiddenByUser.add(id);
        }
        if (this.fadeMs === 0) this.fade.set(id, this.shown(id) ? 1 : 0);
        this.commit(before);
    }

    /** Record a target state; `syncTriggers` mirrors it into the trigger controller for triggered regions. */
    private change(id: string, visible: boolean, syncTriggers: boolean): void {
        if (!this.visible.has(id)) return;
        if (syncTriggers && this.triggered.get(id) && this.triggers.isVisible(id) !== visible) {
            this.syncingTriggers = true;
            try {
                if (visible) this.triggers.show(id);
                else this.triggers.hide(id);
            } finally {
                this.syncingTriggers = false;
            }
        }
        this.visible.set(id, visible);
        if (this.fadeMs === 0) this.fade.set(id, this.shown(id) ? 1 : 0);
    }

    private commit(before: Map<string, boolean>): void {
        if (this.destroyed) return;
        this.writeUniforms();
        this.host.requestRender();
        this.emit(before);
    }

    /** Uniforms for this frame: colours × fades, the surround (strongest dimming wins), area transforms; and the editor bounds. */
    private writeUniforms(): void {
        const resolved = this.resolved;
        if (!resolved) {
            this.bounds.length = 0;
            return;
        }
        const looks = resolved.regions.map((r) => {
            const look = this.getLook(r.id)!;
            const fade = this.fade.get(r.id) ?? 0;
            return { look, fade, dims: !!r.dim };
        });
        const surround = regionSurround(looks.map(({ look, fade, dims }) => ({ dim: dims ? { brightness: look.dimBrightness, saturation: look.dimSaturation } : null, fade })));
        writeRegionsFrame(
            this.uniforms,
            looks.map(({ look, fade }) => ({ rgb: [look.r, look.g, look.b], amount: look.colorStrength * fade })),
            surround,
        );
        // Sphere / box transforms; a region's only volume follows its animated transform.
        this.bounds.length = 0;
        for (const area of resolved.areas) {
            if (area.kind !== 'sphere' && area.kind !== 'box') continue;
            const region = resolved.regions[area.regionIndex];
            const shape = (this.animated.get(region.id)?.size && this.volumeOf(region.id) === area ? this.getAnimatedShape(region.id) : null) ?? area.region;
            writeRegionAreaUniform(this.uniforms, area.slot, shape);
            if (resolved.showBounds && !this.boundsHidden && this.shown(region.id)) {
                this.bounds.push({ ...shape, position: { ...shape.position }, rotation: { ...shape.rotation }, size: { ...shape.size }, color: regionRgb(region.color) });
            }
        }
    }

    private emit(before: Map<string, boolean>): void {
        const changes: RegionVisibilityChange[] = [];
        for (const [id, was] of before) {
            const now = this.visible.get(id);
            if (now !== undefined && now !== was) changes.push({ id, visible: now });
        }
        const ids = this.getVisibleIds();
        for (const listener of this.listeners) {
            try {
                listener(ids, changes);
            } catch {
                /* one listener must not stop the others */
            }
        }
    }
}
